import { Injectable } from '@angular/core';
import { FilePageLinkService } from '../file-page-link.service';
import { TextFocusService } from '../text-focus.service';
import { getChapterUrlSlug } from '../utils/icc-url-utils';
import { ClientLoggingService } from '../client-logging.service';
import {
  CodeEvidenceCitation,
  EVIDENCE_KIND_LABEL,
  EvidenceCitation,
  PageAnchoredCitation,
  describePrecision,
  isPageAnchored,
} from './evidence-citation.model';
import { CODE_LINK_ACTION, MULTILINE_TOOLTIP_CLASS, describeCodeSection } from './code-section-title';

/**
 * Builds the one destination each citation is allowed to have (Issue #1773).
 *
 * This service is the only place a citation becomes something clickable, which is what makes the
 * guarantee "a model-authored URL can never become a live link" checkable rather than aspirational.
 * It takes a validated {@link EvidenceCitation} and never a string, so there is no signature through
 * which prose could supply a destination even by accident.
 *
 * Internal destinations are Angular router links, so SPA navigation, Ctrl-click into a new tab, and
 * a cold load of the same URL in a fresh authenticated session all behave identically. External
 * destinations are checked against a host allow-list and carry safe new-tab attributes.
 */

/** Hosts a citation may link out to. Anything else is refused, not rewritten. */
const ALLOWED_EXTERNAL_HOSTS: readonly string[] = Object.freeze(['codes.iccsafe.org']);

/** `rel` for every external citation link: no window handle, no referrer leak. */
export const EXTERNAL_LINK_REL = 'noopener noreferrer';

/** A destination inside the application, expressed so Angular's router owns the navigation. */
export interface InternalCitationDestination {
  kind: 'internal';
  /** Router link segments, for `[routerLink]`. */
  routerLink: string[];
  /** Query parameters, for `[queryParams]`. Empty object when none are needed. */
  queryParams: Record<string, string>;
  /**
   * The same destination as one absolute path string.
   *
   * Needed for print and export output, where a `routerLink` array means nothing, and for
   * `router.navigateByUrl` when a citation is activated programmatically.
   */
  url: string;
}

/** A destination outside the application, already validated against the host allow-list. */
export interface ExternalCitationDestination {
  kind: 'external';
  href: string;
  target: '_blank';
  rel: string;
}

/** A citation that cannot be linked at all, with the reason a reader can be shown. */
export interface UnlinkableCitationDestination {
  kind: 'none';
  reason: string;
}

export type CitationDestination =
  | InternalCitationDestination
  | ExternalCitationDestination
  | UnlinkableCitationDestination;

/** Everything a template needs to render one citation, precomputed once per data change. */
export interface CitationPresentation {
  citation: EvidenceCitation;
  destination: CitationDestination;
  /** Short marker text shown inline in the prose, e.g. `1`. */
  marker: string;
  /** Visible link text, e.g. `Sheet A2.1, Page 6` or `CBC § 1010.1.1`. */
  visibleLabel: string;
  /**
   * Accessible name, which always starts with {@link visibleLabel} so WCAG 2.5.3 Label in Name is
   * satisfied and a voice user who speaks the visible wording activates the right link.
   */
  ariaLabel: string;
  /** Hover text: what kind of evidence this is and how precise it claims to be. */
  tooltip: string;
  /**
   * Panel class for the tooltip overlay, empty when none is needed.
   *
   * A Material tooltip renders in an overlay attached to the document body, so a component's own
   * styles never reach it — which is why the two-line inline tooltip needs a named class and a
   * global rule rather than anything scoped here. Without it the newline is collapsed and the two
   * lines run together, and every string assertion still passes.
   */
  tooltipClass: string;
  /** Whether this citation opens a page other than the one whose report is being read. */
  crossPage: boolean;
}

/**
 * Turns a validated citation into the one destination it is allowed to have, plus the labels,
 * accessible name, and hover text that go with it. See the module comment above for why this is the
 * only place in the application where a citation becomes clickable.
 */
@Injectable({ providedIn: 'root' })
export class EvidenceCitationLinkService {
  /**
   * Builds the destination for one citation.
   *
   * Every branch produces either a fully-formed destination or an explicit refusal. There is no
   * path that returns `'#'`, because a dead `href` looks identical to a working one until a reader
   * clicks it — the report tab's existing `getIccUrl()` returns `'#'` on missing data, and that is
   * exactly the behaviour this replaces.
   */
  buildDestination(citation: EvidenceCitation): CitationDestination {
    return citation.kind === 'code' ? this.buildCodeDestination(citation) : this.buildPageDestination(citation);
  }

  /**
   * Page, region, and text citations all resolve to the same Preview route, differing only in what
   * they add to it: a region path in the path segments, or an encoded selector in the query string.
   *
   * Region evidence uses the region path rather than a `highlight=x,y,w,h` pixel tuple on purpose.
   * The pixel form from #292 is tied to one rendering of one revision of the page, so it goes stale
   * silently after re-ingestion; a region path is re-resolved against the current hierarchy, which
   * is what the issue asks for and what keeps the highlight correct over time.
   */
  private buildPageDestination(citation: PageAnchoredCitation): CitationDestination {
    const { planId, fileId, pageNumber } = citation.page;
    if (!planId) {
      return { kind: 'none', reason: 'This citation has no project, so its page cannot be opened.' };
    }

    // Legacy single-file projects carry no file ID. File 1 is the same fallback chat page links and
    // FilePageLinkService already use, so a legacy citation opens the page rather than nothing.
    const resolvedFileId = fileId || '1';
    const segments = ['/projects', planId, 'files', resolvedFileId, 'pages', String(pageNumber)];
    if (citation.kind === 'region') segments.push(...citation.regionPath.split('/'));
    segments.push('preview');

    const queryParams: Record<string, string> = {};
    if (citation.kind === 'text') {
      queryParams['text'] = TextFocusService.encodeSelector(citation.selector);
    }

    return { kind: 'internal', routerLink: segments, queryParams, url: this.toUrl(segments, queryParams) };
  }

  /**
   * Builds a canonical code-provision URL from validated catalog identity.
   *
   * The chapter slug goes through {@link getChapterUrlSlug}, which already knows how to tell a
   * human-readable slug from a Code Connect chapter ID and how to derive one from a chapter title.
   * When no chapter slug can be determined the URL degrades to the book, because a fragment hung off
   * the wrong chapter path is a broken link that looks like a working one — the failure #1016 and
   * #559 both fixed.
   */
  private buildCodeDestination(citation: CodeEvidenceCitation): CitationDestination {
    const { documentSlug, chapterSlug, chapterTitle, sectionId } = citation.code;
    const urlSlug = getChapterUrlSlug(chapterTitle, chapterSlug);

    let href = `https://codes.iccsafe.org/content/${encodeURIComponent(documentSlug)}`;
    if (urlSlug) {
      href += `/${encodeURIComponent(urlSlug)}`;
      if (sectionId) href += `#${encodeURIComponent(sectionId)}`;
    }

    if (!this.isAllowedExternalUrl(href)) {
      // Unreachable with the literal prefix above, and deliberately kept anyway: it is the check
      // that has to hold if anyone ever makes the host configurable, and it fails closed.
      ClientLoggingService.log('WARN', '[EvidenceCitationLink] refused code URL outside the allow-list:', href);
      return { kind: 'none', reason: 'This code reference does not resolve to an allowed code host.' };
    }

    return { kind: 'external', href, target: '_blank', rel: EXTERNAL_LINK_REL };
  }

  /** Whether a URL is `https` on an allow-listed host. Any parse failure is a refusal. */
  isAllowedExternalUrl(candidate: string): boolean {
    try {
      const url = new URL(candidate);
      return url.protocol === 'https:' && ALLOWED_EXTERNAL_HOSTS.includes(url.hostname);
    } catch {
      return false;
    }
  }

  /**
   * Precomputes everything a template binds for one citation.
   *
   * `markerIndex` is the citation's one-based position in the report, which is what the reader sees
   * inline. `activePageNumber` and `activeFileId` describe the page whose report is open, and are
   * used only to work out whether this citation leads somewhere else.
   */
  present(
    citation: EvidenceCitation,
    markerIndex: number,
    activeFileId: string,
    activePageNumber: number
  ): CitationPresentation {
    const visibleLabel = this.visibleLabel(citation);
    const kindLabel = EVIDENCE_KIND_LABEL[citation.kind];
    const precision = describePrecision(citation);
    const crossPage =
      isPageAnchored(citation) &&
      (citation.page.pageNumber !== activePageNumber || (citation.page.fileId || '1') !== (activeFileId || '1'));

    // An inline code reference names one provision, so its tooltip names that provision rather than
    // describing the category it belongs to. Every other chip keeps the previous wording: the
    // generic sentence is still the right one for a sources-list entry, which is not hovering over
    // any particular number.
    const inlineCode = citation.kind === 'code' && citation.code.proseInline === true;
    const tooltipParts = inlineCode
      ? [describeCodeSection(citation.code.visibleLabel, citation.code.sectionTitle), CODE_LINK_ACTION]
      : [`${kindLabel} evidence`, precision];
    if (!inlineCode && citation.excerpt) tooltipParts.push(`“${citation.excerpt}”`);
    if (crossPage) tooltipParts.push('Opens a different page from the one you are reading.');

    return {
      citation,
      destination: this.buildDestination(citation),
      marker: String(markerIndex),
      visibleLabel,
      // Prepending the visible label rather than replacing it with the descriptive text is the
      // whole point of WCAG 2.5.3; FilePageLinkService.accessibleName encodes the same rule and is
      // reused so both surfaces stay consistent.
      ariaLabel: FilePageLinkService.accessibleName(
        visibleLabel,
        inlineCode
          ? `${describeCodeSection(citation.code.visibleLabel, citation.code.sectionTitle)}. ${CODE_LINK_ACTION}.`
          : `${kindLabel} evidence. ${precision}`
      ),
      // Two lines rather than one sentence: the provision on the first, what clicking does on the
      // second, which is how the operator asked for it to read on screen.
      tooltip: inlineCode ? tooltipParts.join('\n') : tooltipParts.join(' '),
      tooltipClass: inlineCode ? MULTILINE_TOOLTIP_CLASS : '',
      crossPage,
    };
  }

  /**
   * The wording a citation link shows.
   *
   * Plan evidence leads with the sheet when the page has one, because a reviewer navigates by sheet
   * number far more readily than by page ordinal — "Sheet A2.1, Page 6" is the label the issue asks
   * for. Code evidence shows the catalog's own visible label unchanged, so the section number a
   * reader recognizes is the text they see and can speak.
   */
  visibleLabel(citation: EvidenceCitation): string {
    if (citation.kind === 'code') return citation.code.visibleLabel;

    const { sheetId, pageNumber } = citation.page;
    const pageLabel = sheetId ? `Sheet ${sheetId}, Page ${pageNumber}` : `Page ${pageNumber}`;
    if (citation.kind === 'region') return `${pageLabel} — ${this.regionLeafLabel(citation.regionPath)}`;
    if (citation.kind === 'text') return `${pageLabel} — “${this.truncateForLabel(citation.selector.exact)}”`;
    return pageLabel;
  }

  /**
   * Turns the last segment of a region path into readable wording.
   *
   * Region paths are machine slugs such as `drawing-area/unit-117/bedroom-1`. The leaf is the part
   * that identifies the object to a human; the ancestry is noise in a link label and is available
   * in the citation detail view for anyone who wants it.
   */
  private regionLeafLabel(regionPath: string): string {
    const leaf = regionPath.split('/').filter(Boolean).pop() ?? regionPath;
    return leaf.replace(/[-_]+/g, ' ').trim();
  }

  /** Keeps a quoted selector short enough to sit inline without pushing the layout around. */
  private truncateForLabel(text: string): string {
    const collapsed = text.replace(/\s+/g, ' ').trim();
    return collapsed.length <= 48 ? collapsed : `${collapsed.slice(0, 47)}…`;
  }

  /** Renders router segments and query parameters as one absolute path, for print and export. */
  private toUrl(segments: readonly string[], queryParams: Record<string, string>): string {
    const path = segments
      .map((segment, index) => (index === 0 ? segment : encodeURIComponent(segment)))
      .join('/')
      .replace(/^\/+/, '/');
    const query = Object.entries(queryParams)
      .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
      .join('&');
    return query ? `${path}?${query}` : path;
  }
}
